// Client layer (TS-2.5.03) - PM+ workflow, BRD section 8.5. Owns the generate-preview-then-publish
// sequence: a title/reportDate/nextReportText form calls publicationApi.generate for a DeckPreview
// (never a published version by itself), then, once a preview exists, offers DeckPublishDialog to
// turn it into one. `pinnedDetailProjectKeys`/`excludedDetailProjectKeys` are left empty here - this
// batch does not build a project picker for manual detail-slide overrides; the server's derived
// selection (DetailSelectionSpec.mode "Derived") still applies with no manual overrides submitted.
import { useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "../../api/ApiError";
import type { DeckPreview } from "../../api/generated/contracts";
import { publicationApi } from "../../api/publication";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { WarningsBanner } from "../../components/WarningsBanner";
import { strings } from "../../strings";
import { DeckPreviewPanel } from "./DeckPreviewPanel";
import { DeckPublishDialog } from "./DeckPublishDialog";

export interface DeckGeneratePanelProps {
  readonly onPublished: () => void;
}

const G = strings.decks.generate;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DeckGeneratePanel({ onPublished }: DeckGeneratePanelProps) {
  const [title, setTitle] = useState("");
  const [reportDate, setReportDate] = useState(todayIso());
  const [nextReportText, setNextReportText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<ApiError | null>(null);
  const [preview, setPreview] = useState<DeckPreview | null>(null);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<ApiError | null>(null);
  const [published, setPublished] = useState(false);

  async function handleGenerate(event: FormEvent) {
    event.preventDefault();
    setGenerating(true);
    setGenerateError(null);
    setPublished(false);
    try {
      const next = await publicationApi.generate({
        reportDate,
        title,
        nextReportText: nextReportText.trim().length > 0 ? nextReportText.trim() : null,
        pinnedDetailProjectKeys: [],
        excludedDetailProjectKeys: [],
      });
      setPreview(next);
    } catch (cause) {
      setGenerateError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The preview could not be generated."));
    } finally {
      setGenerating(false);
    }
  }

  async function handlePublish(publishReportDate: string, markFinal: boolean) {
    if (!preview) return;
    setPublishing(true);
    setPublishError(null);
    try {
      await publicationApi.publish({
        previewKey: preview.previewKey,
        reportDate: publishReportDate,
        title,
        markFinal,
        nextReportText: nextReportText.trim().length > 0 ? nextReportText.trim() : null,
        pinnedDetailProjectKeys: [],
        excludedDetailProjectKeys: [],
      });
      setPublished(true);
      setPreview(null);
      onPublished();
    } catch (cause) {
      setPublishError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The deck could not be published."));
    } finally {
      setPublishing(false);
    }
  }

  return (
    <section className="eg-deck-generate">
      <h2>{G.heading}</h2>

      {published && <WarningsBanner items={[{ key: "published", tone: "info", message: "Published." }]} />}

      <form onSubmit={(event) => void handleGenerate(event)}>
        <FormField id="eg-deck-title" label={G.title} required>
          <input
            type="text"
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            {...formFieldControlProps("eg-deck-title")}
          />
        </FormField>

        <FormField id="eg-deck-generate-report-date" label={G.reportDate} required>
          <input
            type="date"
            required
            value={reportDate}
            onChange={(event) => setReportDate(event.target.value)}
            {...formFieldControlProps("eg-deck-generate-report-date")}
          />
        </FormField>

        <FormField id="eg-deck-next-report" label={G.nextReportText}>
          <textarea
            value={nextReportText}
            onChange={(event) => setNextReportText(event.target.value)}
            {...formFieldControlProps("eg-deck-next-report")}
          />
        </FormField>

        <button type="submit" disabled={generating || title.trim().length === 0}>
          {generating ? G.generating : G.submit}
        </button>
      </form>

      {generateError && (
        <div role="alert">
          <p>{G.errorTitle}</p>
          <p>{generateError.message}</p>
        </div>
      )}

      {preview && (
        <>
          <DeckPreviewPanel preview={preview} />

          {preview.publishBlockers.length === 0 && (
            <DeckPublishDialog initialReportDate={reportDate} submitting={publishing} onPublish={(...args) => void handlePublish(...args)} />
          )}

          {publishError && (
            <div role="alert">
              <p>{strings.decks.publish.errorTitle}</p>
              <p>{publishError.message}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
